import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Separator } from './ui/separator';
import {
  User, Bell, Mail, Plus, Trash2, Pencil, Check, X,
  Server, Eye, EyeOff, LogOut, Save, ChevronRight, ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

const Section = ({ title, description, children }) => (
  <div className="mb-8">
    <div className="mb-4">
      <h2 className="text-[15px] font-semibold text-foreground">{title}</h2>
      {description && <p className="text-[13px] text-muted-foreground mt-0.5">{description}</p>}
    </div>
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      {children}
    </div>
  </div>
);

const Row = ({ label, description, children, last }) => (
  <div className={cn("flex items-center gap-4 px-4 py-3.5", !last && "border-b border-border")}>
    <div className="flex-1 min-w-0">
      <p className="text-[14px] font-medium text-foreground leading-tight">{label}</p>
      {description && <p className="text-[12px] text-muted-foreground mt-0.5 leading-tight">{description}</p>}
    </div>
    <div className="flex-shrink-0">{children}</div>
  </div>
);

function PushSubscribeButton() {
  const [loading, setLoading] = useState(false);
  const handleActivate = async () => {
    if (!('Notification' in window)) {
      toast.error('Notifications non supportées par ce navigateur');
      return;
    }
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      toast.error('Notifications push non disponibles (utilisez la PWA sur iPhone)');
      return;
    }
    let permission = Notification.permission;
    if (permission === 'default') {
      permission = await Notification.requestPermission();
    }
    if (permission === 'denied') {
      toast.error('Autorisez les notifications dans les paramètres du navigateur puis réessayez.');
      return;
    }
    if (permission !== 'granted') return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/push-vapid-public`);
      if (!data?.publicKey) {
        toast.error('Service notifications indisponible');
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const key = urlBase64ToUint8Array(data.publicKey);
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: key,
      });
      const json = sub.toJSON();
      await axios.post(`${API}/push-subscription`, {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
      });
      toast.success('Notifications push activées. Vous recevrez une alerte à chaque nouveau mail.');
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Erreur';
      toast.error(msg === 'Notifications push non configurées' ? 'Service temporairement indisponible.' : `Échec: ${msg}`);
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      type="button"
      onClick={handleActivate}
      disabled={loading}
      className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-[13px] font-medium disabled:opacity-60"
    >
      {loading ? 'Activation...' : 'Activer les notifications push'}
    </button>
  );
}

function PushTestButton() {
  const [loading, setLoading] = useState(false);
  const handleTest = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/push-test`);
      toast.success(data.sent ? `Notification test envoyée (${data.sent}) — vérifiez votre appareil.` : 'Envoyé.');
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Erreur';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      type="button"
      onClick={handleTest}
      disabled={loading}
      className="px-4 py-2 rounded-lg border border-border bg-background text-foreground text-[13px] font-medium hover:bg-accent disabled:opacity-60"
    >
      {loading ? 'Envoi...' : 'Notification test'}
    </button>
  );
}

// Carte compte avec édition inline
const AccountCard = ({ acct, onDelete, onUpdate, last }) => {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(acct.label);
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (label.trim() !== acct.label) payload.label = label.trim();
      if (password) payload.password = password;
      if (Object.keys(payload).length === 0) { setEditing(false); setSaving(false); return; }
      await axios.patch(`${API}/accounts/${acct.id}`, payload);
      toast.success('Compte mis à jour');
      setEditing(false);
      setPassword('');
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally { setSaving(false); }
  };

  return (
    <div className={cn("px-4 py-3", !last && "border-b border-border")}>
      <div className="flex items-center gap-3">
        {/* Avatar */}
        <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center text-primary font-bold text-sm flex-shrink-0">
          {(acct.label || acct.email)[0].toUpperCase()}
        </div>
        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-medium text-foreground truncate">{acct.label}</p>
          <p className="text-[12px] text-muted-foreground truncate">{acct.email}</p>
          {!acct.is_alias && !acct.is_default && (
            <p className="text-[11px] text-muted-foreground/60">{acct.imap_host}</p>
          )}
        </div>
        {/* Badge */}
        {acct.is_default && (
          <span className="text-[11px] bg-primary/15 text-primary px-2 py-0.5 rounded-full font-medium">Principal</span>
        )}
        {acct.is_alias && (
          <span className="text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded-full">Alias</span>
        )}
        {/* Actions */}
        {!acct.is_default && !acct.is_alias && (
          <button
            onClick={() => setEditing(!editing)}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-accent transition-colors text-muted-foreground"
            title="Modifier"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
        {!acct.is_default && (
          <button
            onClick={() => onDelete(acct)}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-100 hover:text-red-600 transition-colors text-muted-foreground"
            title="Supprimer"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Edit form inline */}
      {editing && (
        <div className="mt-3 pl-12 space-y-2.5">
          <div>
            <Label className="text-xs text-muted-foreground">Nom affiché</Label>
            <Input value={label} onChange={e => setLabel(e.target.value)} className="h-8 mt-1 text-sm" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Nouveau mot de passe (laisser vide = inchangé)</Label>
            <div className="relative mt-1">
              <Input
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="h-8 text-sm pr-8"
                placeholder="••••••••"
              />
              <button
                onClick={() => setShowPwd(!showPwd)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                {showPwd ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={save}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium disabled:opacity-60"
            >
              <Check className="w-3.5 h-3.5" />
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
            <button
              onClick={() => { setEditing(false); setLabel(acct.label); setPassword(''); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium"
            >
              <X className="w-3.5 h-3.5" />
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Formulaire ajout compte
const AddAccountForm = ({ onAdded, onCancel }) => {
  const [form, setForm] = useState({ email: '', password: '', label: '' });
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const submit = async () => {
    if (!form.email.trim() || !form.password) { toast.error('Email et mot de passe requis'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/accounts`, { email: form.email.trim(), password: form.password, label: form.label?.trim() || undefined });
      toast.success('Compte ajouté !');
      onAdded();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connexion impossible. Vérifiez les identifiants.');
    } finally { setLoading(false); }
  };

  return (
    <div className="px-4 py-4 border-t border-border space-y-3 bg-muted/30">
      <p className="text-[13px] font-semibold text-foreground">Nouveau compte IMAP</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-xs text-muted-foreground">Adresse email *</Label>
          <Input type="email" placeholder="devis@renoviapro.fr" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="h-9 mt-1 text-sm" />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Nom affiché</Label>
          <Input placeholder="Devis RenoviaPro" value={form.label} onChange={e => setForm({...form, label: e.target.value})} className="h-9 mt-1 text-sm" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs text-muted-foreground">Mot de passe *</Label>
          <div className="relative mt-1">
            <Input type={showPwd ? 'text' : 'password'} placeholder="••••••••" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="h-9 text-sm pr-8" />
            <button onClick={() => setShowPwd(!showPwd)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
              {showPwd ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground">Le serveur IMAP/SMTP est détecté automatiquement.</p>
      <div className="flex gap-2">
        <button onClick={submit} disabled={loading} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-[13px] font-medium disabled:opacity-60">
          <Plus className="w-3.5 h-3.5" />
          {loading ? 'Connexion...' : 'Ajouter'}
        </button>
        <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-border text-[13px]">Annuler</button>
      </div>
    </div>
  );
};

export const SettingsView = () => {
  const { user, logout, logoutAllSessions, accounts, fetchAccounts } = useAuth();
  const [activeSection, setActiveSection] = useState('comptes');
  const [settings, setSettings] = useState({ signature: '', notifications_enabled: true });
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [isSaving, setIsSaving] = useState(false);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [pageSize, setPageSize] = useState(() => parseInt(localStorage.getItem('renoviamail_page_size') || '50', 10));
  const [security, setSecurity] = useState(null);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [totpSetup, setTotpSetup] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  const [disableTotpCode, setDisableTotpCode] = useState('');

  const fetchSettings = useCallback(async () => {
    try { const r = await axios.get(`${API}/settings`); setSettings(r.data); } catch {}
  }, []);

  const fetchSecurity = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/security`);
      setSecurity(r.data);
      setRecoveryEmail(r.data.recovery_email || '');
    } catch {}
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);
  useEffect(() => { fetchSecurity(); }, [fetchSecurity]);
  useEffect(() => { if (user?.full_name) setFullName(user.full_name); }, [user?.full_name]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const p = [axios.patch(`${API}/settings`, settings)];
      if (fullName.trim() && fullName !== user?.full_name) p.push(axios.patch(`${API}/auth/profile`, { full_name: fullName }));
      await Promise.all(p);
      toast.success('Paramètres enregistrés');
    } catch { toast.error('Erreur lors de l\'enregistrement'); }
    finally { setIsSaving(false); }
  };

  const handleDeleteAccount = async (acct) => {
    if (!window.confirm(`Supprimer ${acct.email} ?`)) return;
    try {
      if (acct.is_alias) await axios.delete(`${API}/accounts/aliases/${acct.id}`);
      else await axios.delete(`${API}/accounts/${acct.id}`);
      toast.success('Supprimé');
      fetchAccounts();
    } catch { toast.error('Erreur suppression'); }
  };

  const handleLogoutAll = async () => {
    if (!window.confirm('Déconnecter tous les appareils connectés à RenoviaMail ?')) return;
    try {
      await logoutAllSessions();
      toast.success('Tous les appareils ont été déconnectés');
    } catch {
      toast.error('Impossible de déconnecter tous les appareils');
    }
  };

  const handleSendRecoveryCode = async () => {
    try {
      await axios.post(`${API}/auth/recovery-email`, { email: recoveryEmail });
      toast.success('Code envoyé à l’email de secours');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible d’envoyer le code');
    }
  };

  const handleVerifyRecoveryCode = async () => {
    try {
      await axios.post(`${API}/auth/recovery-email/verify`, { code: recoveryCode });
      toast.success('Email de secours vérifié');
      setRecoveryCode('');
      fetchSecurity();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Code incorrect');
    }
  };

  const handleSetupTotp = async () => {
    try {
      const { data } = await axios.post(`${API}/auth/totp/setup`);
      setTotpSetup(data);
      toast.success('Scannez le QR code puis validez le code');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible de préparer Authenticator');
    }
  };

  const handleEnableTotp = async () => {
    try {
      await axios.post(`${API}/auth/totp/enable`, { code: totpCode });
      toast.success('Authenticator activé');
      setTotpSetup(null);
      setTotpCode('');
      fetchSecurity();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Code Authenticator incorrect');
    }
  };

  const handleDisableTotp = async () => {
    if (!window.confirm('Désactiver Authenticator ?')) return;
    try {
      await axios.post(`${API}/auth/totp/disable`, { code: disableTotpCode });
      toast.success('Authenticator désactivé');
      setDisableTotpCode('');
      fetchSecurity();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Code Authenticator incorrect');
    }
  };

  const handleRevokeDevice = async (device) => {
    if (!window.confirm(`Déconnecter ${device.device || 'cet appareil'} ?`)) return;
    try {
      await axios.delete(`${API}/auth/sessions/${device.id}`);
      toast.success('Appareil déconnecté');
      fetchSecurity();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible de déconnecter cet appareil');
    }
  };

  const nav = [
    { id: 'comptes', label: 'Comptes mail', icon: Mail },
    { id: 'profil', label: 'Profil', icon: User },
    { id: 'securite', label: 'Sécurité', icon: ShieldCheck },
    { id: 'signature', label: 'Signature', icon: Pencil },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'affichage', label: 'Affichage', icon: Eye },
  ];

  const renderContent = () => {
    if (activeSection === 'comptes') return (
      <>
        <Section title="Boîtes mail" description="Gérez vos comptes IMAP connectés">
          {accounts.map((acct, i) => (
            <AccountCard
              key={acct.id}
              acct={acct}
              onDelete={handleDeleteAccount}
              onUpdate={fetchAccounts}
              last={i === accounts.length - 1 && !showAddAccount}
            />
          ))}
          {showAddAccount ? (
            <AddAccountForm onAdded={() => { fetchAccounts(); setShowAddAccount(false); }} onCancel={() => setShowAddAccount(false)} />
          ) : (
            <div className="px-4 py-3 border-t border-border">
              <button
                onClick={() => setShowAddAccount(true)}
                className="flex items-center gap-2 text-[13px] text-primary font-medium hover:underline"
              >
                <Plus className="w-4 h-4" />
                Ajouter un compte
              </button>
            </div>
          )}
        </Section>
      </>
    );

    if (activeSection === 'profil') return (
      <Section title="Informations personnelles">
        <Row label="Nom complet" description="Affiché dans vos emails envoyés">
          <Input value={fullName} onChange={e => setFullName(e.target.value)} className="h-8 w-48 text-sm" />
        </Row>
        <Row label="Adresse email" description="Adresse principale du compte" last>
          <Input value={user?.email || ''} disabled className="h-8 w-48 text-sm" />
        </Row>
        <div className="px-4 py-3 border-t border-border flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-3">
            <button onClick={logout} className="flex items-center gap-2 text-[13px] text-red-500 font-medium hover:text-red-600">
              <LogOut className="w-4 h-4" />
              Déconnexion
            </button>
            <button onClick={handleLogoutAll} className="flex items-center gap-2 text-[13px] text-red-600 font-medium hover:text-red-700">
              <LogOut className="w-4 h-4" />
              Déconnecter tous les appareils
            </button>
          </div>
          <button onClick={handleSave} disabled={isSaving} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-[13px] font-medium disabled:opacity-60">
            <Save className="w-3.5 h-3.5" />
            {isSaving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </Section>
    );

    if (activeSection === 'securite') return (
      <>
        <Section title="Sécurité de connexion" description="Protégez RenoviaMail même si un mot de passe est volé">
          <Row label="Email de secours" description={security?.recovery_email_verified ? "Vérifié, utilisé pour approuver les accès inhabituels" : "Utilisé pour approuver un nouvel appareil ou une IP inhabituelle"}>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Input value={recoveryEmail} onChange={e => setRecoveryEmail(e.target.value)} placeholder="adresse@exemple.fr" className="h-8 w-56 text-sm" />
              <button onClick={handleSendRecoveryCode} className="px-3 py-2 rounded-lg border border-border text-[12px] hover:bg-accent">Envoyer code</button>
            </div>
          </Row>
          <Row label="Valider email de secours" description="Entrez le code reçu sur cette adresse">
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Input value={recoveryCode} onChange={e => setRecoveryCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="123456" className="h-8 w-28 text-sm text-center tracking-[0.25em]" />
              <button onClick={handleVerifyRecoveryCode} className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-[12px]">Vérifier</button>
            </div>
          </Row>
          <Row label="Application Authenticator" description={security?.totp_enabled ? "Activé: code TOTP demandé à la connexion" : "Gratuit, plus sûr que SMS"}>
            {security?.totp_enabled ? (
              <div className="flex flex-wrap items-center justify-end gap-2">
                <Input value={disableTotpCode} onChange={e => setDisableTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Code" className="h-8 w-24 text-sm text-center" />
                <button onClick={handleDisableTotp} className="px-3 py-2 rounded-lg border border-red-500/40 text-red-500 text-[12px]">Désactiver</button>
              </div>
            ) : (
              <button onClick={handleSetupTotp} className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-[12px]">Activer</button>
            )}
          </Row>
          {totpSetup && (
            <div className="px-4 py-4 border-b border-border">
              <p className="text-[13px] text-muted-foreground mb-3">Scannez ce QR code avec Google Authenticator, Microsoft Authenticator ou Aegis.</p>
              <div className="flex flex-wrap gap-4 items-center">
                <img src={totpSetup.qr_data_url} alt="QR code Authenticator" className="w-36 h-36 rounded-lg bg-white p-2" />
                <div className="flex-1 min-w-[220px]">
                  <p className="text-[12px] text-muted-foreground mb-2">Clé manuelle: <span className="font-mono text-foreground">{totpSetup.secret}</span></p>
                  <div className="flex gap-2">
                    <Input value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Code 6 chiffres" className="h-9 w-40 text-sm text-center" />
                    <button onClick={handleEnableTotp} className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-[12px]">Confirmer</button>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="px-4 py-4 border-t border-border">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <p className="text-[14px] font-medium text-foreground">Appareils connectés</p>
                <p className="text-[12px] text-muted-foreground">IP, navigateur, emplacement approximatif et dernière activité.</p>
              </div>
              <span className="text-[13px] text-muted-foreground">{security?.trusted_devices_count || 0}</span>
            </div>
            <div className="space-y-2">
              {(security?.trusted_devices || []).length === 0 ? (
                <p className="text-[13px] text-muted-foreground">Aucun appareil enregistré pour le moment.</p>
              ) : (
                security.trusted_devices.map((device) => (
                  <div key={device.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-background px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium text-foreground truncate">{device.device || 'Appareil inconnu'}</p>
                      <p className="text-[12px] text-muted-foreground truncate">IP {device.ip || 'inconnue'} · {device.location || 'Localisation inconnue'}</p>
                      <p className="text-[11px] text-muted-foreground/70 truncate">Dernière activité: {device.last_seen_at ? new Date(device.last_seen_at).toLocaleString() : 'inconnue'}</p>
                    </div>
                    <button onClick={() => handleRevokeDevice(device)} className="px-3 py-1.5 rounded-lg border border-red-500/40 text-red-500 text-[12px] hover:bg-red-500/10">
                      Déconnecter
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </Section>
      </>
    );

    if (activeSection === 'signature') return (
      <Section title="Signature" description="Ajoutée automatiquement à chaque email envoyé">
        <div className="p-4">
          <Textarea
            placeholder={"Cordialement,\nWassim Serrour\nRENOVIA PRO | 06 52 03 60 20"}
            value={settings.signature || ''}
            onChange={e => setSettings({ ...settings, signature: e.target.value })}
            className="min-h-[140px] text-sm resize-none"
          />
          <div className="mt-3 flex justify-end">
            <button onClick={handleSave} disabled={isSaving} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-[13px] font-medium disabled:opacity-60">
              <Save className="w-3.5 h-3.5" />
              {isSaving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </Section>
    );

    if (activeSection === 'affichage') return (
      <Section title="Affichage" description="Personnalisez l'affichage de votre boîte mail">
        <Row label="Emails par page" description="Nombre d'emails affichés par page dans la liste" last>
          <select
            value={pageSize}
            onChange={e => {
              const v = parseInt(e.target.value, 10);
              setPageSize(v);
              localStorage.setItem('renoviamail_page_size', v);
              toast.success(`${v} emails par page — rechargez la page pour appliquer`);
            }}
            className="h-8 px-3 rounded-lg border border-border bg-background text-sm text-foreground"
          >
            {[10, 15, 20, 25, 50, 100].map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </Row>
      </Section>
    );

    if (activeSection === 'notifications') return (
      <Section title="Notifications">
        <Row label="Nouvelles emails" description="Notifications pour les messages reçus">
          <Switch
            checked={settings.notifications_enabled || false}
            onCheckedChange={v => setSettings({ ...settings, notifications_enabled: v })}
          />
        </Row>
        <Row label="Sons" description="Son à l'arrivée d'un email">
          <Switch />
        </Row>
        <div className="px-4 py-3.5 border-b border-border">
          <p className="text-[14px] font-medium text-foreground mb-1">Notifications push (iPhone / PWA)</p>
          <p className="text-[12px] text-muted-foreground mb-3">Recevez une alerte instantanée à chaque nouveau mail. Cliquez pour activer.</p>
          <div className="flex flex-wrap gap-2">
            <PushSubscribeButton />
            <PushTestButton />
          </div>
        </div>
        <div className="px-4 py-3 border-t border-border flex justify-end">
          <button onClick={handleSave} disabled={isSaving} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-[13px] font-medium disabled:opacity-60">
            <Save className="w-3.5 h-3.5" />
            {isSaving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </Section>
    );
  };

  return (
    <div className="flex flex-col h-full bg-background" data-testid="settings-view">
      {/* Nav onglets horizontal (mobile) + latérale (desktop) */}
      <div className="flex-shrink-0">
        {/* Mobile : onglets scrollables en haut */}
        <div className="md:hidden border-b border-border bg-card">
          <div className="flex items-center px-2 pt-3 pb-0 gap-0.5 overflow-x-auto scrollbar-hide">
            <span className="text-[14px] font-semibold text-foreground mr-3 flex-shrink-0 pb-2 pl-1">Paramètres</span>
            {nav.map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-2 rounded-t-lg text-[12px] font-medium whitespace-nowrap flex-shrink-0 transition-colors border-b-2",
                    activeSection === item.id
                      ? "border-primary text-primary bg-primary/8"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  )}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Desktop : nav latérale */}
        <div className="hidden md:flex w-56 border-r border-border flex-shrink-0 bg-card flex-col">
          <div className="p-4 border-b border-border">
            <h1 className="text-[15px] font-semibold text-foreground">Paramètres</h1>
          </div>
          <nav className="p-2 space-y-0.5">
            {nav.map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-medium transition-colors text-left",
                    activeSection === item.id ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  )}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {item.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 min-h-0">
          <div className="max-w-2xl">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};
